import type { TransportItem } from "@grafana/faro-web-sdk";
import { redactUrl, scrub } from "./scrub";

const URL_LIKE = /^(?:[a-z][a-z0-9+.-]*:\/\/|\/)/i;
const DROPPED_SPAN_ATTRIBUTES = new Set(["http.user_agent", "user_agent.original"]);

const clean = (value: string) => (URL_LIKE.test(value) ? redactUrl(value) : scrub(value));

function cleanRecord<T extends Record<string, unknown> | undefined>(record: T): T {
  if (!record) return record;
  return Object.fromEntries(
    Object.entries(record).map(([key, value]) => [key, typeof value === "string" ? clean(value) : value]),
  ) as T;
}

interface OtlpAttribute { key: string; value?: { stringValue?: string } }
interface OtlpPayload { resourceSpans?: { scopeSpans?: { spans?: { attributes?: OtlpAttribute[] }[] }[] }[] }

function cleanSpans(payload: OtlpPayload) {
  for (const resource of payload.resourceSpans ?? []) {
    for (const scope of resource.scopeSpans ?? []) {
      for (const span of scope.spans ?? []) {
        span.attributes = span.attributes
          ?.filter(attribute => !DROPPED_SPAN_ATTRIBUTES.has(attribute.key))
          .map(attribute => typeof attribute.value?.stringValue === "string"
            ? { ...attribute, value: { ...attribute.value, stringValue: clean(attribute.value.stringValue) } }
            : attribute);
      }
    }
  }
}

/** Last line of defence before anything leaves the browser: no query strings, IDs in URLs, PII in text, or user agent. */
export function sanitizeTransportItem(item: TransportItem): TransportItem {
  const { page, browser } = item.meta;
  if (page?.url) item.meta = { ...item.meta, page: { ...page, url: redactUrl(page.url) } };
  if (browser?.userAgent) item.meta = { ...item.meta, browser: { ...browser, userAgent: undefined } };

  const payload = item.payload as Record<string, unknown>;
  switch (item.type) {
    case "exception":
      if (typeof payload["value"] === "string") payload["value"] = scrub(payload["value"]);
      payload["context"] = cleanRecord(payload["context"] as Record<string, unknown> | undefined);
      break;
    case "log":
      if (typeof payload["message"] === "string") payload["message"] = scrub(payload["message"]);
      payload["context"] = cleanRecord(payload["context"] as Record<string, unknown> | undefined);
      break;
    case "event":
      payload["attributes"] = cleanRecord(payload["attributes"] as Record<string, unknown> | undefined);
      break;
    case "measurement":
      payload["context"] = cleanRecord(payload["context"] as Record<string, unknown> | undefined);
      break;
    case "trace":
      cleanSpans(payload as OtlpPayload);
      break;
  }
  return item;
}
